import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    const hydrate = () => {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource && !source.src) source.setAttribute('src', videoSource);
      }
      try { videoElement.load(); } catch (e) { /* noop */ }
    };

    // Gate hydration on viewport entry so off-screen background videos don't
    // pull multi-MB range requests during initial page load.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            obs.disconnect();
            hydrate();
            break;
          }
        }
      }, { rootMargin: '0px' });
      io.observe(this);
    } else {
      hydrate();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
